// src/features/auth/pages/AgreeTermsPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import { Header, withBase, HEADER } from "@/ui/Header";
import { PageShell } from "@/ui/PageShell";
import { usePageScale } from "@/ui/usePageScale";
import { COLOR, FW } from "@/ui/tokens";

import {
    APP_ID_SS_KEY,
    DEFAULT_APP_ID,
    buildAgreements,
    resolveConsentPolicy,
    type ConsentItem,
} from "../consentPolicy";
import { consentSubmit } from "@/lib/api";

/* ── 고정 스펙 ───────────────────────── */
const BASE = {
    W: 1264,
    MARGIN_X: 108,
    BLOCK_W: 1048,

    ROW_PAD_Y: 22,
    ROW_BORDER: 1,

    TITLE_FZ: 44,

    BTN_W: 848,
    BTN_H: 124,

    GAP_Y: 24,
} as const;

/** 1264 기준 스케일링 */
function useScale() {
    // max: 1.2 는 기존 로직과 동일한 상한
    const { s, px, sfpx } = usePageScale({ baseW: BASE.W, max: 1.2 });
    const spx = px;
    return { s, px, spx, sfpx };
}

/** 좌/우로 갈라진 체크박스 행 */
function CheckRow({
                      id, checked, onChange, label, right, padY, fontPx, borderTop,
                  }: {
    id: string;
    checked: boolean;
    onChange: (v: boolean) => void;
    label: React.ReactNode;
    right?: React.ReactNode;
    padY: number;
    fontPx: number;
    borderTop?: boolean;
}) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                paddingTop: padY,
                paddingBottom: padY,
                borderTop: borderTop ? `${BASE.ROW_BORDER}px solid #e5e5e5` : "none",
            }}
        >
            <label
                htmlFor={id}
                style={{ display: "flex", alignItems: "center", cursor: "pointer", flex: 1, minWidth: 0 }}
            >
                <input
                    id={id}
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ width: fontPx, height: fontPx }}
                />
                <span style={{ marginLeft: 12, fontSize: fontPx, fontWeight: 800 }}>{label}</span>
            </label>

            {right ? <div style={{ marginLeft: 12 }}>{right}</div> : null}
        </div>
    );
}

const SS_KEY = "bt.agree.checks";

/**
 * "로그인했는데 동의가 없어서 넘어온 상태" 마커.
 *
 * 이 화면은 두 가지 경로로 진입한다:
 *   signup  — 가입 전 동의 (다음 → /auth/register)
 *   consent — 이미 계정이 있는데 이 서비스 동의가 없음 (다음 → POST /consents)
 *
 * 모드는 router state 로 들어오는데, /legal/* 를 다녀오면 state 가
 * { agreed: 'terms' } 로 덮여서 날아간다(LegalTermsPage.tsx:121).
 * 그래서 app_id 와 마찬가지로 세션에 남겨야 왕복을 견딘다.
 */
const CONSENT_SS_KEY = "bt.consent.pending";

type Checks = Record<string, boolean>;

/**
 * 체크 상태는 app_id 와 함께 저장한다.
 * 저장된 app_id 가 지금 정책과 다르면 버린다 — 다른 서비스에서 찍은 체크가
 * 넘어오면 안 된다.
 */
function loadChecks(appId: string): Checks {
    try {
        const raw = JSON.parse(sessionStorage.getItem(SS_KEY) || "null");
        if (!raw || raw.app_id !== appId) return {};
        return (raw.checks || {}) as Checks;
    } catch {
        return {};
    }
}
function saveChecks(appId: string, checks: Checks) {
    sessionStorage.setItem(SS_KEY, JSON.stringify({ app_id: appId, checks }));
}

export default function AgreeTermsPage() {
    const nav = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { s, spx, sfpx } = useScale();

    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // 진입 모드 확정.
    //   state.mode === 'consent' → 마커 저장
    //   state.mode === 'signup'  → 마커 제거 (가입 플로우로 새로 들어옴)
    //   mode 없음                → /legal/* 왕복 복귀. 기존 마커를 유지한다.
    const entry = useMemo(() => {
        const st = (location.state as any) || {};

        if (st.mode === "consent") {
            const v = { appId: String(st.appId || DEFAULT_APP_ID), missing: (st.missing || []) as string[] };
            sessionStorage.setItem(CONSENT_SS_KEY, JSON.stringify(v));
            return { mode: "consent" as const, ...v };
        }
        if (st.mode === "signup") {
            sessionStorage.removeItem(CONSENT_SS_KEY);
            return { mode: "signup" as const, appId: "", missing: [] as string[] };
        }
        try {
            const raw = JSON.parse(sessionStorage.getItem(CONSENT_SS_KEY) || "null");
            if (raw?.appId) {
                return { mode: "consent" as const, appId: String(raw.appId), missing: (raw.missing || []) as string[] };
            }
        } catch {}
        return { mode: "signup" as const, appId: "", missing: [] as string[] };
    }, [location.state]);

    // app_id 해석.
    //
    // 세션 폴백이 필요한 이유: /legal/terms 의 "동의하고 돌아가기"가
    // nav("/auth/agree", { state: { agreed } }) 로만 돌아와서(LegalTermsPage.tsx:121)
    // 쿼리가 날아간다. 쿼리만 믿으면 전문보기 한 번에 정책이 리셋된다.
    //
    // 단 폴백을 무조건 쓰면 안 된다. 한 번 ?app_id=X 로 들어온 탭은 그 뒤로
    // 쿼리 없이 들어와도 계속 X 로 남는다. 그래서 "왕복 복귀" 일 때만 세션을 본다.
    // 왕복 복귀는 router state 의 agreed 로 식별한다 — 이 값은 새로고침해도
    // history.state 에 남아 있어서 리로드에도 견딘다.
    const appId = useMemo(() => {
        if (entry.mode === "consent") return entry.appId;

        const fromQuery = (searchParams.get("app_id") || "").trim();
        if (fromQuery) {
            sessionStorage.setItem(APP_ID_SS_KEY, fromQuery);
            return fromQuery;
        }

        const returningFromLegal = !!(location.state as any)?.agreed;
        if (returningFromLegal) {
            return (sessionStorage.getItem(APP_ID_SS_KEY) || "").trim();
        }

        // 쿼리도 없고 왕복 복귀도 아니면 새 진입이다. 남아 있던 값을 지우고 기본값으로.
        sessionStorage.removeItem(APP_ID_SS_KEY);
        return "";
    }, [entry, searchParams, location.state]);

    const { policy } = useMemo(
        () => resolveConsentPolicy(appId),
        [appId],
    );

    // consent 모드에서는 서버가 "빠졌다"고 한 항목만 다시 받는다.
    // 이미 동의한 항목을 또 물어보지 않는다.
    const visibleItems = useMemo(() => {
        if (entry.mode !== "consent" || !entry.missing.length) return policy.items;
        const want = new Set(entry.missing);
        const filtered = policy.items.filter((i) => want.has(i.code));
        // 정책과 서버 목록이 어긋나면(정책 파일이 낡음) 전체를 보여주는 쪽으로 떨어진다.
        return filtered.length ? filtered : policy.items;
    }, [entry, policy]);

    const [checks, setChecks] = useState<Checks>(() => loadChecks(policy.app_id));

    const setOne = (code: string, v: boolean) => {
        setChecks((prev) => {
            const next = { ...prev, [code]: v };
            saveChecks(policy.app_id, next);
            return next;
        });
    };

    // 법적 페이지에서 동의 후 돌아왔을 때 자동 체크 (router state)
    useEffect(() => {
        const agreed = (location.state as { agreed?: string } | null)?.agreed;
        if (!agreed) return;
        // 정책에 없는 code 가 넘어오면 무시한다.
        if (!policy.items.some((i) => i.code === agreed)) return;
        setOne(agreed, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, policy]);

    // "전체 동의"는 실제로 렌더된 항목만 대상으로 한다.
    // (숨겨진 항목이 전체 동의에 딸려 들어가면 본 적 없는 항목에 동의하게 된다)
    const allOn = useMemo(
        () => visibleItems.length > 0 && visibleItems.every((i) => !!checks[i.code]),
        [visibleItems, checks],
    );

    const canNext = useMemo(
        () => visibleItems.filter((i) => i.required).every((i) => !!checks[i.code]),
        [visibleItems, checks],
    );

    const toggleAll = (next: boolean) => {
        const v: Checks = { ...checks };
        visibleItems.forEach((i) => { v[i.code] = next; });
        setChecks(v);
        saveChecks(policy.app_id, v);
    };

    const goNext = async () => {
        if (busy) return;

        // 가입 전 동의: 기존 플로우 그대로 /auth/register 로 넘긴다.
        if (entry.mode !== "consent") {
            const agreements = buildAgreements(policy, checks);
            const fromEvent = !!(location.state as any)?.fromEvent;
            sessionStorage.removeItem(SS_KEY);
            sessionStorage.setItem("bt.agreements", JSON.stringify(agreements));
            nav("/auth/register", { state: { agreements, fromEvent, appId: policy.app_id }, replace: true });
            return;
        }

        // 로그인 후 동의: 이미 계정이 있으므로 바로 기록한다.
        setBusy(true);
        setErr(null);
        try {
            const items = visibleItems.map((i) => ({ code: i.code, agreed: !!checks[i.code] }));
            await consentSubmit(policy.app_id, items);
            sessionStorage.removeItem(SS_KEY);
            sessionStorage.removeItem(CONSENT_SS_KEY);
            nav("/search", { replace: true });
        } catch (e: any) {
            setErr(e?.status === 401
                ? "로그인이 만료되었습니다. 다시 로그인해주세요."
                : "동의 저장에 실패했습니다. 잠시 후 다시 시도해주세요.");
        } finally {
            setBusy(false);
        }
    };

    const renderRow = (item: ConsentItem) => {
        const right = item.legalPath
            ? (
                <button
                    onClick={() => nav(item.legalPath!)}
                    style={{ fontSize: sfpx(18), textDecoration: "underline", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                    전문보기
                </button>
            )
            : item.note
                ? <span style={{ fontSize: sfpx(18), color: "#666" }}>{item.note}</span>
                : undefined;

        return (
            <CheckRow
                key={item.code}
                id={item.code}
                checked={!!checks[item.code]}
                onChange={(v) => setOne(item.code, v)}
                borderTop
                label={<span>[{item.required ? "필수" : "선택"}] {item.label}</span>}
                right={right}
                padY={s(BASE.ROW_PAD_Y)}
                fontPx={s(28)}
            />
        );
    };

    return (
        <PageShell
            title="이용 동의"
            onBack={() => nav(-1)}
            backIcon={withBase("icons/prev1.svg")}
            width={spx(BASE.W)}
        >
            <div style={{ width: spx(BASE.BLOCK_W), margin: "0 auto", paddingLeft: spx(BASE.MARGIN_X), paddingRight: spx(BASE.MARGIN_X) }}>
                <h1 style={{ fontSize: sfpx(BASE.TITLE_FZ), margin: 0, marginBottom: spx(16), fontWeight: 800 }}>
                    {entry.mode === "consent" ? "약관 동의가 필요합니다" : "이용 동의"}
                </h1>

                {entry.mode === "consent" ? (
                    <div style={{ fontSize: sfpx(24), fontWeight: 700, color: "#444", lineHeight: 1.35, marginBottom: spx(16) }}>
                        {policy.name} 서비스를 이용하려면 아래 약관에 동의해주세요.
                    </div>
                ) : null}

                <CheckRow id="all" checked={allOn} onChange={toggleAll} label={"전체 동의"} padY={s(BASE.ROW_PAD_Y)} fontPx={s(32)} />
                <div style={{ height: 1, background: "#e5e5e5" }} />

                {visibleItems.map(renderRow)}

                {err ? (
                    <div style={{ marginTop: spx(24), fontSize: sfpx(22), color: "#d00", fontWeight: 700 }}>{err}</div>
                ) : null}

                <div style={{ display: "grid", placeItems: "center", marginTop: spx(BASE.GAP_Y * 2), marginBottom: spx(40) }}>
                    <button
                        disabled={!canNext || busy}
                        onClick={goNext}
                        style={{
                            width: spx(BASE.BTN_W), height: spx(BASE.BTN_H), borderRadius: spx(12),
                            background: COLOR.black, color: COLOR.white, fontWeight: FW.extraBold, fontSize: sfpx(32),
                            opacity: canNext && !busy ? 1 : 0.5, border: "none",
                        }}
                    >
                        {busy ? "처리 중..." : entry.mode === "consent" ? "동의하고 시작하기" : "다음"}
                    </button>
                </div>
            </div>
        </PageShell>
    );
}